"use client";

/**
 * MODAL BEHAVIOR REQUIREMENTS:
 *
 * 1. CLEAR ALL BUTTON:
 *    - Clears all filters and updates URL
 *    - Does NOT close the modal
 *    - User must manually close modal after clearing
 *
 * 2. CLOSING MODAL:
 *    - Mobile: Only via close button (X) in top-right
 *    - Desktop: Only via clicking outside modal area
 *    - Clear All does NOT close modal on either platform
 *
 * 3. APPLY FILTERS BUTTON:
 *    - Applies selected filters and updates URL
 *    - ALWAYS closes modal on both platforms
 *
 * This design allows users to:
 * - Clear filters and continue browsing other filter options
 * - Explicitly choose when to close the modal
 * - Apply filters as the final confirmation step
 */
import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import { DialogContent, DialogOverlay, DialogTrigger } from "@/components/rac/dialog";
import { Sheet, SheetContent, SheetOverlay } from "@/components/rac/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/radix/tooltip";
import { Badge } from "@/components/radix/badge";
import { DesktopFilters, Filters } from "@/components/search/filters/desktop";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useUrlFilters } from "@/hooks/use-url-filters";
import React, { useCallback } from "react";
import { MobileFilters } from "./mobile";
interface PropertyFiltersPopoverProps {
  trigger: React.ReactNode;
  defaultValues?: Partial<Filters>;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  tooltip?: string;
}
export function PropertyFiltersPopover(t0) {
  const $ = _c(60);
  const {
    trigger,
    defaultValues,
    isOpen,
    onOpenChange,
    tooltip
  } = t0;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const {
    setFilters
  } = useUrlFilters();
  const open = isOpen;
  const setOpen = onOpenChange;
  const desktopFormResetRef = React.useRef(null);
  const mobileFormResetRef = React.useRef(null);
  let t1;
  if ($[0] !== setOpen) {
    t1 = _data => {
      setOpen(false);
    };
    $[0] = setOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClose = t1;
  let t2;
  if ($[2] !== isMobile || $[3] !== setFilters) {
    t2 = () => {
      if (isMobile && mobileFormResetRef.current) {
        mobileFormResetRef.current();
      } else {
        if (!isMobile && desktopFormResetRef.current) {
          desktopFormResetRef.current();
        }
      }
      setFilters({});
    };
    $[2] = isMobile;
    $[3] = setFilters;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleClearFilters = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set(["locations", "propertyType", "bedrooms", "bathroomsMin", "budget", "builtupAreaMin", "floors", "balconiesMin", "parkingsMin", "rentalValue", "facing", "furnishing", "status", "amenities", "documents", "spaces", "waterSources", "levelType", "maxAge", "possession", "plotFacing"]);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const validFilterKeys = t3;
  let t4;
  if ($[6] !== defaultValues) {
    t4 = defaultValues ? Object.entries(defaultValues).filter(t5 => {
      const [key, value] = t5;
      if (!validFilterKeys.has(key)) {
        return false;
      }
      if (key === "bounds") {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === "object" && value !== null) {
        if ("min" in value && "max" in value) {
          const rangeValue = value as {
            min: number;
            max: number;
          };
          return (rangeValue?.min ?? 0) > 0 || (rangeValue?.max ?? 0) > 0;
        }
        return Object.keys(value).length > 0;
      }
      if (typeof value === "string") {
        return !!value;
      }
      if (typeof value === "number") {
        return value > 0;
      }
      return false;
    }).length : 0;
    $[6] = defaultValues;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const activeFiltersCount = t4;
  let t5;
  if ($[8] !== activeFiltersCount) {
    t5 = activeFiltersCount > 0 && <Badge className="absolute -top-1 -right-1 z-10">{activeFiltersCount}</Badge>;
    $[8] = activeFiltersCount;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5 || $[11] !== trigger) {
    t6 = <div className="relative">{trigger}{t5}</div>;
    $[10] = t5;
    $[11] = trigger;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const triggerElement = t6;
  let t7;
  if ($[13] !== tooltip || $[14] !== triggerElement) {
    t7 = tooltip ? <TooltipTrigger asChild={true}>{triggerElement}</TooltipTrigger> : triggerElement;
    $[13] = tooltip;
    $[14] = triggerElement;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const triggerNode = t7;
  let t8;
  if ($[16] !== tooltip) {
    t8 = content => {
      if (!tooltip) {
        return content;
      }
      return <TooltipProvider><Tooltip delayDuration={300}>{content}<TooltipContent side="bottom"><p>{tooltip}</p></TooltipContent></Tooltip></TooltipProvider>;
    };
    $[16] = tooltip;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const renderWithTooltip = t8;
  if (isMobile) {
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = resetFn => {
        mobileFormResetRef.current = resetFn;
      };
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== handleClose || $[20] !== open) {
      t10 = <div className="flex-1 overflow-hidden"><MobileFilters isOpen={open} onApplyFilters={handleClose} onClose={handleClose} onResetForm={t9} /></div>;
      $[19] = handleClose;
      $[20] = open;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] !== handleClearFilters) {
      t11 = <Button variant="link" className="flex-1" onPress={handleClearFilters}>Clear All</Button>;
      $[22] = handleClearFilters;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Button variant="default" className="flex-1" onPress={_temp}>Apply Filters</Button>;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    if ($[25] !== t11) {
      t13 = <div className="flex-shrink-0 p-4 border-t bg-white flex gap-3">{t11}{t12}</div>;
      $[25] = t11;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    let t14;
    if ($[27] !== t10 || $[28] !== t13) {
      t14 = <SheetOverlay><SheetContent side="right" className="w-full bg-white border-l" closeButton={false}><div className="flex flex-col h-full">{t10}{t13}</div></SheetContent></SheetOverlay>;
      $[27] = t10;
      $[28] = t13;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    let t15;
    if ($[30] !== open || $[31] !== setOpen || $[32] !== t14 || $[33] !== triggerNode) {
      t15 = <Sheet isOpen={open} onOpenChange={setOpen}>{triggerNode}{t14}</Sheet>;
      $[30] = open;
      $[31] = setOpen;
      $[32] = t14;
      $[33] = triggerNode;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== renderWithTooltip || $[36] !== t15) {
      t16 = renderWithTooltip(t15);
      $[35] = renderWithTooltip;
      $[36] = t15;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    return t16;
  }
  let t9;
  if ($[38] !== defaultValues) {
    t9 = defaultValues || {};
    $[38] = defaultValues;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = resetFn_0 => {
      desktopFormResetRef.current = resetFn_0;
    };
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== handleClose || $[42] !== t9) {
    t11 = <div className="flex-1 overflow-y-auto"><DesktopFilters onApplyFilters={handleClose} defaultValues={t9} className="border-0 rounded-none w-full max-w-md md:max-w-4xl lg:max-w-6xl" isMobile={false} onClose={handleClose} onResetForm={t10} /></div>;
    $[41] = handleClose;
    $[42] = t9;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== handleClearFilters) {
    t12 = <Button variant="link" className="flex-1" onPress={handleClearFilters}>Clear All</Button>;
    $[44] = handleClearFilters;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Button variant="default" className="flex-1" onPress={_temp4}>Apply Filters</Button>;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== t12) {
    t14 = <div className="flex-shrink-0 p-4 border-t bg-white flex gap-3">{t12}{t13}</div>;
    $[47] = t12;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t11 || $[50] !== t14) {
    t15 = <DialogOverlay><DialogContent side="top" className="w-full max-w-md md:max-w-4xl lg:max-w-6xl h-auto max-h-[90dvh] p-0 overflow-hidden mx-auto relative z-50" closeButton={false}><div className="flex flex-col h-full max-h-[90dvh]" onMouseDown={_temp2} onClick={_temp3}>{t11}{t14}</div></DialogContent></DialogOverlay>;
    $[49] = t11;
    $[50] = t14;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  let t16;
  if ($[52] !== open || $[53] !== setOpen || $[54] !== t15 || $[55] !== triggerNode) {
    t16 = <DialogTrigger isOpen={open} onOpenChange={setOpen}>{triggerNode}{t15}</DialogTrigger>;
    $[52] = open;
    $[53] = setOpen;
    $[54] = t15;
    $[55] = triggerNode;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== renderWithTooltip || $[58] !== t16) {
    t17 = renderWithTooltip(t16);
    $[57] = renderWithTooltip;
    $[58] = t16;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  return t17;
}
function _temp4() {
  const form = document.querySelector("#property-filters-form") as HTMLFormElement;
  if (form) {
    form.requestSubmit();
  }
}
function _temp3(e_0) {
  const dropdown_0 = window.settlinDropdownRef;
  if (dropdown_0 && dropdown_0.contains(e_0.target as Node)) {
    const target_0 = e_0.target as HTMLElement;
    const isDropdownItem_0 = target_0.closest("[role=\"option\"]") || target_0.closest(".cursor-pointer") || target_0.classList.contains("cursor-pointer");
    if (!isDropdownItem_0) {
      e_0.stopPropagation();
      e_0.preventDefault();
    }
  }
}
function _temp2(e) {
  const dropdown = window.settlinDropdownRef;
  if (dropdown && dropdown.contains(e.target as Node)) {
    const target = e.target as HTMLElement;
    const isDropdownItem = target.closest("[role=\"option\"]") || target.closest(".cursor-pointer") || target.classList.contains("cursor-pointer");
    if (!isDropdownItem) {
      e.stopPropagation();
      e.preventDefault();
    }
  }
}
function _temp() {
  const mobileForm = document.querySelector("#mobile-filters-form") as HTMLFormElement;
  if (mobileForm) {
    mobileForm.requestSubmit();
  }
}